// Qonto banking sync panel — list transactions, sync on demand, manual & auto reconcile.
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const fmt = (v) => Number(v || 0).toFixed(2);

export default function QontoSyncPanel({ locale = "fr", onReconciled }) {
  const isFr = locale === "fr";
  const [accounts, setAccounts] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [txData, setTxData] = useState({ items: [], pagination: { page: 1, total_pages: 0, total_count: 0 }, aggregates: { total_debit: 0, total_credit: 0 } });
  const [filterIban, setFilterIban] = useState("");
  const [filterReconciled, setFilterReconciled] = useState(""); // "", "true", "false"
  const [filterSide, setFilterSide] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [proposals, setProposals] = useState(null);
  const [linkTx, setLinkTx] = useState(null); // transaction object being linked manually

  const loadAccounts = useCallback(async () => {
    const r = await api.get("/admin/accounting/qonto/accounts");
    setAccounts(r.data.items || []);
    setLastSync(r.data.last_sync || null);
  }, []);

  const loadInvoices = useCallback(async () => {
    const r = await api.get("/admin/accounting/invoices");
    setInvoices(r.data.items || []);
  }, []);

  const loadTransactions = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), per_page: "25" });
    if (filterIban) params.set("iban", filterIban);
    if (filterReconciled === "true") params.set("reconciled", "true");
    if (filterReconciled === "false") params.set("reconciled", "false");
    if (filterSide) params.set("side", filterSide);
    if (search.trim()) params.set("q", search.trim());
    const r = await api.get(`/admin/accounting/qonto/transactions?${params}`);
    setTxData(r.data);
  }, [page, filterIban, filterReconciled, filterSide, search]);

  useEffect(() => { loadAccounts(); loadInvoices(); }, [loadAccounts, loadInvoices]);
  useEffect(() => { loadTransactions(); }, [loadTransactions]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/accounting/qonto/sync?days_back=365");
      const m = isFr
        ? `Sync OK — ${r.data.accounts} compte(s), ${r.data.transactions_new} nouvelles transactions sur ${r.data.transactions_seen} vues.`
        : `Sync OK — ${r.data.accounts} account(s), ${r.data.transactions_new} new transactions out of ${r.data.transactions_seen}.`;
      alert(m);
      await loadAccounts();
      await loadTransactions();
    } catch (e) {
      alert((isFr ? "Sync échec : " : "Sync failed: ") + (e.response?.data?.detail || e.message));
    } finally { setSyncing(false); }
  };

  const propose = async () => {
    setProposing(true);
    try {
      const r = await api.post("/admin/accounting/qonto/auto-reconcile?apply=false");
      setProposals(r.data.proposals || []);
    } catch (e) {
      alert((isFr ? "Échec : " : "Failed: ") + (e.response?.data?.detail || e.message));
    } finally { setProposing(false); }
  };

  const applyProposals = async () => {
    if (!proposals?.length) return;
    if (!window.confirm(isFr ? `Appliquer ${proposals.length} rapprochement(s) ?` : `Apply ${proposals.length} match(es)?`)) return;
    try {
      const r = await api.post("/admin/accounting/qonto/auto-reconcile?apply=true");
      alert((isFr ? "Appliqué : " : "Applied: ") + r.data.applied);
      setProposals(null);
      await loadTransactions();
      await loadInvoices();
      onReconciled?.();
    } catch (e) {
      alert((isFr ? "Échec : " : "Failed: ") + (e.response?.data?.detail || e.message));
    }
  };

  const unreconcile = async (tx) => {
    if (!tx.reconciled_invoice_id) return;
    if (!window.confirm(isFr ? "Détacher cette transaction de la facture ?" : "Detach this transaction from the invoice?")) return;
    try {
      await api.post("/admin/accounting/qonto/unreconcile", {
        transaction_qonto_id: tx.qonto_id,
        invoice_id: tx.reconciled_invoice_id,
      });
      await loadTransactions();
      await loadInvoices();
      onReconciled?.();
    } catch (e) {
      alert((isFr ? "Échec : " : "Failed: ") + (e.response?.data?.detail || e.message));
    }
  };

  const reconcileManual = async (invoiceId) => {
    if (!linkTx) return;
    try {
      await api.post("/admin/accounting/qonto/reconcile", {
        transaction_qonto_id: linkTx.qonto_id,
        invoice_id: invoiceId,
      });
      setLinkTx(null);
      await loadTransactions();
      await loadInvoices();
      onReconciled?.();
    } catch (e) {
      alert((isFr ? "Échec : " : "Failed: ") + (e.response?.data?.detail || e.message));
    }
  };

  const totalBalance = useMemo(() => accounts.reduce((acc, a) => acc + (a.balance || 0), 0), [accounts]);
  const invoiceById = useMemo(() => {
    const m = new Map();
    (invoices || []).forEach((i) => m.set(i.id, i));
    return m;
  }, [invoices]);

  const toPayInvoices = useMemo(() => (invoices || []).filter((i) => i.status === "to_pay"), [invoices]);

  return (
    <div className="space-y-4" data-testid="qonto-panel">
      <div className="flex flex-wrap items-center gap-3"
           style={{ paddingTop: "1rem", borderTop: "1px dashed var(--border)" }}>
        <h3 className="font-display text-xl">{isFr ? "Banque (Qonto)" : "Banking (Qonto)"}</h3>
        <span className="text-xs" style={{ color: "var(--muted)" }}>
          {lastSync
            ? (isFr ? `Dernière sync : ${new Date(lastSync).toLocaleString("fr-FR")}` : `Last sync: ${new Date(lastSync).toLocaleString()}`)
            : (isFr ? "Jamais synchronisé" : "Never synced")}
        </span>
        <button className="ml-auto btn btn-primary btn-sm" onClick={handleSync} disabled={syncing}
                data-testid="qonto-sync-btn">
          {syncing ? "⏳…" : (isFr ? "↻ Synchroniser" : "↻ Sync")}
        </button>
        <button className="btn btn-outline btn-sm" onClick={propose} disabled={proposing}
                data-testid="qonto-propose-btn">
          {proposing ? "⏳…" : (isFr ? "🪄 Rapprochement auto" : "🪄 Auto reconcile")}
        </button>
      </div>

      {/* Account summary */}
      {accounts.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {accounts.map((a) => (
            <button key={a.qonto_id}
                    onClick={() => { setFilterIban(filterIban === a.iban ? "" : a.iban); setPage(1); }}
                    className="admin-card text-left"
                    style={{ borderColor: filterIban === a.iban ? "var(--gold)" : "var(--border)" }}
                    data-testid={`qonto-account-${a.slug}`}>
              <div className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
                {a.name}
              </div>
              <div className="font-display text-xl" style={{ color: "var(--moss)" }}>{fmt(a.balance)} €</div>
              <div className="text-[0.7rem] font-mono" style={{ color: "var(--muted)" }}>{a.iban}</div>
            </button>
          ))}
          <div className="admin-card">
            <div className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
              {isFr ? "Total" : "Total"}
            </div>
            <div className="font-display text-xl">{fmt(totalBalance)} €</div>
          </div>
        </div>
      )}

      {/* Auto-reconcile proposals */}
      {proposals !== null && (
        <div className="admin-card space-y-2" style={{ borderColor: "var(--gold)" }}>
          <div className="font-caps text-[0.7rem]" style={{ letterSpacing: "0.18em", color: "var(--gold)" }}>
            {isFr ? `${proposals.length} PROPOSITION(S) DE RAPPROCHEMENT` : `${proposals.length} RECONCILIATION PROPOSAL(S)`}
          </div>
          {proposals.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--muted)" }}>
              {isFr ? "Aucune correspondance trouvée." : "No matches found."}
            </p>
          ) : (
            <>
              <ul className="space-y-1 text-xs">
                {proposals.map((p) => (
                  <li key={p.transaction_qonto_id} className="flex flex-wrap gap-2 items-center"
                      data-testid={`qonto-proposal-${p.transaction_qonto_id}`}>
                    <span className="font-mono">{fmt(p.transaction_amount)} €</span>
                    <span>·</span>
                    <span>{p.transaction_label}</span>
                    <span style={{ color: "var(--muted)" }}>→</span>
                    <span>{p.invoice_supplier} {p.invoice_number && `(${p.invoice_number})`}</span>
                    <span className="ml-auto" style={{ color: "var(--gold)" }}>★ {Math.round(p.score * 100)}%</span>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <button className="btn btn-primary btn-sm" onClick={applyProposals} data-testid="qonto-apply-btn">
                  {isFr ? "Appliquer tout" : "Apply all"}
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => setProposals(null)}>
                  {isFr ? "Annuler" : "Cancel"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="font-caps text-[0.6rem] block" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>
            {isFr ? "Recherche" : "Search"}
          </span>
          <input className="input-classic" placeholder={isFr ? "Libellé, réf…" : "Label, ref…"}
                 value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                 data-testid="qonto-search" />
        </label>
        <label className="block">
          <span className="font-caps text-[0.6rem] block" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>
            {isFr ? "Sens" : "Side"}
          </span>
          <select className="input-classic" value={filterSide}
                  onChange={(e) => { setFilterSide(e.target.value); setPage(1); }}
                  data-testid="qonto-filter-side">
            <option value="">{isFr ? "Tous" : "All"}</option>
            <option value="debit">{isFr ? "Débit" : "Debit"}</option>
            <option value="credit">{isFr ? "Crédit" : "Credit"}</option>
          </select>
        </label>
        <label className="block">
          <span className="font-caps text-[0.6rem] block" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>
            {isFr ? "Rapproché" : "Reconciled"}
          </span>
          <select className="input-classic" value={filterReconciled}
                  onChange={(e) => { setFilterReconciled(e.target.value); setPage(1); }}
                  data-testid="qonto-filter-rec">
            <option value="">{isFr ? "Tous" : "All"}</option>
            <option value="true">{isFr ? "Oui" : "Yes"}</option>
            <option value="false">{isFr ? "Non" : "No"}</option>
          </select>
        </label>
        {filterIban && (
          <button className="btn btn-outline btn-sm" onClick={() => { setFilterIban(""); setPage(1); }}
                  data-testid="qonto-clear-iban">
            ✕ IBAN
          </button>
        )}
      </div>

      {/* Aggregates */}
      <div className="flex gap-3 text-xs">
        <span><strong style={{ color: "var(--cinnabar)" }}>−{fmt(txData.aggregates.total_debit)} €</strong> {isFr ? "débit" : "debit"}</span>
        <span><strong style={{ color: "var(--moss)" }}>+{fmt(txData.aggregates.total_credit)} €</strong> {isFr ? "crédit" : "credit"}</span>
        <span style={{ color: "var(--muted)" }}>· {txData.pagination.total_count} {isFr ? "transactions" : "transactions"}</span>
      </div>

      {/* Transactions table */}
      <div className="admin-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              {["Date", isFr ? "Libellé" : "Label", isFr ? "Montant" : "Amount", isFr ? "Statut" : "Status", ""].map((h, i) => (
                <th key={i} className="text-left p-2 font-caps text-[0.6rem]"
                    style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {txData.items.map((tx) => {
              const linkedInv = tx.reconciled_invoice_id ? invoiceById.get(tx.reconciled_invoice_id) : null;
              const amt = (tx.side === "debit" ? -1 : 1) * Math.abs(tx.amount || 0);
              return (
                <tr key={tx.qonto_id} style={{ borderBottom: "1px solid var(--border)" }}
                    data-testid={`qonto-tx-${tx.qonto_id}`}>
                  <td className="p-2 text-xs" style={{ color: "var(--ink-2)" }}>
                    {(tx.settled_at || tx.emitted_at || "").slice(0, 10) || "—"}
                  </td>
                  <td className="p-2">
                    <div className="font-medium">{tx.counterparty_name || tx.label || "—"}</div>
                    {tx.reference && <div className="text-[0.7rem]" style={{ color: "var(--muted)" }}>{tx.reference}</div>}
                  </td>
                  <td className="p-2 text-sm font-mono"
                      style={{ color: amt < 0 ? "var(--cinnabar)" : "var(--moss)" }}>
                    {amt >= 0 ? "+" : ""}{fmt(amt)} €
                  </td>
                  <td className="p-2">
                    {linkedInv ? (
                      <span className="px-2 py-0.5 text-[0.65rem] font-caps"
                            style={{ backgroundColor: "rgba(83,128,72,0.16)", color: "#4f6b3a", letterSpacing: "0.15em" }}>
                        ✓ {linkedInv.supplier_name} {linkedInv.invoice_number && `#${linkedInv.invoice_number}`}
                      </span>
                    ) : tx.reconciled_invoice_id ? (
                      <span className="px-2 py-0.5 text-[0.65rem] font-caps"
                            style={{ backgroundColor: "rgba(83,128,72,0.16)", color: "#4f6b3a", letterSpacing: "0.15em" }}>
                        ✓ {isFr ? "Rapproché" : "Reconciled"}
                      </span>
                    ) : tx.side === "debit" ? (
                      <span className="px-2 py-0.5 text-[0.65rem] font-caps"
                            style={{ backgroundColor: "var(--surface-2)", color: "var(--muted)", letterSpacing: "0.15em" }}>
                        {isFr ? "Non rapproché" : "Unmatched"}
                      </span>
                    ) : ""}
                  </td>
                  <td className="p-2 text-right">
                    {!tx.reconciled_invoice_id && tx.side === "debit" && toPayInvoices.length > 0 && (
                      <button className="btn btn-outline btn-sm" onClick={() => setLinkTx(tx)}
                              data-testid={`qonto-link-${tx.qonto_id}`}>
                        🔗
                      </button>
                    )}
                    {tx.reconciled_invoice_id && (
                      <button className="btn btn-outline btn-sm" onClick={() => unreconcile(tx)}
                              data-testid={`qonto-unlink-${tx.qonto_id}`}>
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {txData.items.length === 0 && (
              <tr><td colSpan="5" className="p-6 text-center" style={{ color: "var(--muted)" }}>
                {isFr ? "Aucune transaction. Cliquez sur Synchroniser." : "No transactions. Click Sync."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {txData.pagination.total_pages > 1 && (
        <div className="flex gap-2 items-center justify-center">
          <button className="btn btn-outline btn-sm" disabled={page <= 1}
                  onClick={() => setPage(page - 1)} data-testid="qonto-prev">‹</button>
          <span className="text-xs">{page} / {txData.pagination.total_pages}</span>
          <button className="btn btn-outline btn-sm"
                  disabled={page >= txData.pagination.total_pages}
                  onClick={() => setPage(page + 1)} data-testid="qonto-next">›</button>
        </div>
      )}

      {/* Manual reconcile modal */}
      {linkTx && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ backgroundColor: "rgba(20,16,12,0.7)" }}>
          <div className="admin-card max-w-lg w-full max-h-[80vh] overflow-y-auto space-y-3"
               data-testid="qonto-link-modal">
            <h3 className="font-display text-lg">
              {isFr ? "Lier à une facture" : "Link to invoice"}
            </h3>
            <div className="text-sm" style={{ color: "var(--muted)" }}>
              <strong>{fmt(Math.abs(linkTx.amount))} €</strong> · {linkTx.counterparty_name || linkTx.label} · {(linkTx.settled_at || "").slice(0, 10)}
            </div>
            <div className="space-y-1">
              {toPayInvoices.map((inv) => {
                const close = Math.abs((inv.amount_ttc || 0) - Math.abs(linkTx.amount || 0)) < 0.5;
                return (
                  <button key={inv.id}
                          className="w-full text-left p-2 text-sm flex items-center gap-2"
                          style={{
                            backgroundColor: close ? "rgba(201,138,58,0.12)" : "var(--surface-2)",
                            border: "1px solid var(--border)",
                          }}
                          onClick={() => reconcileManual(inv.id)}
                          data-testid={`qonto-pick-inv-${inv.id}`}>
                    <span className="font-medium">{inv.supplier_name}</span>
                    {inv.invoice_number && <span style={{ color: "var(--muted)" }}>#{inv.invoice_number}</span>}
                    <span className="ml-auto font-mono">{fmt(inv.amount_ttc)} €</span>
                  </button>
                );
              })}
              {toPayInvoices.length === 0 && (
                <p className="text-sm" style={{ color: "var(--muted)" }}>
                  {isFr ? "Aucune facture à payer." : "No invoices to pay."}
                </p>
              )}
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => setLinkTx(null)}>
              {isFr ? "Annuler" : "Cancel"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
